/**
 * The values stored in the server's `system_settings` table.
 *
 * Installation-wide configuration, as opposed to the per-user settings in the
 * browser's `appSettings` and the per-project ones in `.taskin/config.json`.
 * The shapes live here rather than beside the service because the admin UI has
 * to name the same ones it reads and writes, and it cannot import from
 * `apps/server`.
 *
 * Every key is a JSON object even when it currently holds one field: growing a
 * setting then costs a field in a struct instead of a change to the row's type,
 * which would have to be migrated.
 */
import { Schema } from "effect";

import {
  EnvironmentId,
  IsoDateTime,
  ManagedAgentId,
  ProjectId,
  TrimmedNonEmptyString,
  UserId,
} from "./baseSchemas";

/**
 * The `system_settings.key` for the models the server uses on its own behalf.
 *
 * A string constant and not an enum: the table is generic, and the set of keys
 * is only ever enumerated by the code that owns each one.
 */
export const GENERATION_DEFAULTS_SETTING_KEY = "generation-defaults";

/**
 * Which model does the server's own short-form writing — thread titles, commit
 * messages, PR text, branch names.
 *
 * Codex-only, hence a bare slug rather than a `{ provider, model }` pair: every
 * one of these calls goes through `TextGenerationService`, which has a single
 * Codex implementation. Widening this to other providers is a change to that
 * service first, and to this shape second.
 *
 * There is no per-user override. These calls happen with no client present
 * (a thread title is generated server-side, after the first turn), so a
 * browser-held preference could not be honoured consistently anyway — half the
 * calls would use it and half would not.
 */
export const GenerationDefaultsSetting = Schema.Struct({
  smallModel: TrimmedNonEmptyString,
});
export type GenerationDefaultsSetting = typeof GenerationDefaultsSetting.Type;

/**
 * `GET` / `PUT /api/admin/system-settings/generation-defaults`.
 *
 * The response is enveloped like the agent routes' `{ agent }` rather than
 * returning the value bare, so a second setting can join the payload without
 * the existing field moving. `PUT` takes `GenerationDefaultsSetting` directly:
 * the whole setting is replaced, since a one-field document has no meaningful
 * patch semantics.
 *
 * A `GET` always answers — an installation that has never been configured gets
 * the effective default, not a 404. The screen shows what the server will
 * actually use, which is the only number an admin can act on.
 */
export const AdminGenerationDefaultsResponse = Schema.Struct({
  generationDefaults: GenerationDefaultsSetting,
});
export type AdminGenerationDefaultsResponse = typeof AdminGenerationDefaultsResponse.Type;

export const FILE_PREVIEW_SETTING_KEY = "file-preview";

/**
 * Glob patterns an absolute path must match (see `matchesAnyGlob` in
 * `apps/server/src/workspace/globAllowlist.ts`) for `files.preview` to read
 * it. Empty by default — an installation that has never configured this
 * grants no access to anything outside a project's own workspace.
 *
 * A project's `.taskin/config.json` `filePreviewAllowedGlobs` is additive to
 * this list, never a replacement: a project can widen what it previews, not
 * narrow what the admin already allowed.
 */
export const FilePreviewSetting = Schema.Struct({
  allowedGlobs: Schema.Array(TrimmedNonEmptyString),
});
export type FilePreviewSetting = typeof FilePreviewSetting.Type;

export const AdminFilePreviewResponse = Schema.Struct({
  filePreview: FilePreviewSetting,
});
export type AdminFilePreviewResponse = typeof AdminFilePreviewResponse.Type;

export const THREAD_RECAP_SETTING_KEY = "thread-recap";

/**
 * Whether the server writes a thread recap — a one-line "where this thread
 * stands" — when a turn settles.
 *
 * Off by default, and deliberately so: unlike a thread title, which is written
 * once per thread, a recap is written every time a turn settles in a thread
 * somebody has stopped watching. Each one is a `codex exec` of its own, so an
 * installation that has never opted in spends nothing. The spend is visible
 * under the `thread-recap` usage kind.
 *
 * This is the master switch, not the whole answer. Off here means nothing is
 * generated anywhere. On here means the `threads.recap` grant decides, person
 * by person: the reactor only counts members who hold it, so a thread whose
 * whole audience lacks the grant still costs nothing. Two levels rather than
 * one because the two questions have different owners — whether the
 * installation does this at all, and which of its people it is worth doing for.
 */
export const ThreadRecapSetting = Schema.Struct({
  enabled: Schema.Boolean,
});
export type ThreadRecapSetting = typeof ThreadRecapSetting.Type;

export const AdminThreadRecapResponse = Schema.Struct({
  threadRecap: ThreadRecapSetting,
});
export type AdminThreadRecapResponse = typeof AdminThreadRecapResponse.Type;

export const QA_AUTOMATION_SETTING_KEY = "qa-automation";

/**
 * What a project's GitHub automation is allowed to do, and as whom.
 *
 * Per project rather than per installation, and off unless somebody turned it
 * on: this is the switch that lets a comment written on GitHub — by anybody
 * who can comment on the repository — start work inside Taskin. Two of the
 * three fields exist because that sentence is only acceptable if the answer to
 * "as whom, with what" is a decision an admin made in writing:
 *
 * - `actorUserId` is the account the automation acts as. Its permissions are
 *   the ones that apply, checked per cycle, exactly as if that person had
 *   pressed the button. **Turning this on is delegating to that account
 *   whatever people ask for from GitHub**, so it should hold the grants the
 *   work needs (`p.env.use`, `p.pr.preview`) and nothing more — not an admin.
 * - `agentId` is the managed agent that carries the work out. It has to have
 *   the QA tools for the operation (`managedAgentQaCapability`), which is
 *   checked when this is written *and* again on every cycle, because an
 *   agent's tool selection is edited elsewhere and later.
 *
 * The GitHub login of whoever asked is **not** resolved to a Taskin account.
 * There is no mapping between the two, and guessing one by email would
 * attribute work — and therefore permissions — to the wrong person in silence.
 * The login travels as raw text in the first message of the thread instead
 * ("a petición de @fulano en el PR #131"), so the record says who asked while
 * the authority stays the configured actor's.
 */
export const QaAutomationProjectSetting = Schema.Struct({
  enabled: Schema.Boolean,
  agentId: ManagedAgentId,
  actorUserId: UserId,
  /**
   * The project environment the automation works inside.
   *
   * Not derivable, and not optional. A thread opened for a QA operation must
   * name an environment — `authorizeQaOperationCommand` refuses one that does
   * not — and the tools take "the environment this thread works in" from it:
   * `qa_preview_create` branches and copies its data from there when no
   * `baseName` is given. A project may have up to three of them, so "the
   * project's environment" is not a question the server can answer on its own;
   * picking the wrong one would build previews from `staging`'s data because it
   * happened to be created first.
   */
  environmentId: EnvironmentId,
  /**
   * Whether a pull request merging into the project's base branch also asks the
   * agent to bring the environment up to date.
   *
   * Off by default and a switch of its own rather than part of `enabled`,
   * because it is a different power. Everything else this automation does
   * happens in a preview: an isolated worktree with its own database and its
   * own URL, which expires on its own and which nobody else is looking at. A
   * sync moves the **shared** environment onto new code, for everybody using
   * it at once. Turning previews on from GitHub must not carry that with it as
   * a bonus.
   *
   * It is also the only thing that widens what the actor account needs: with
   * this off, `p.env.sync` is not asked for, and with it on a missing
   * `p.env.sync` switches the automation off with a reason like any other
   * revoked grant.
   */
  syncOnMerge: Schema.Boolean,
  /**
   * Why the server switched this off by itself, and when.
   *
   * The automation disables itself rather than degrading when what it was
   * pointed at stops being valid — the actor's account is deactivated, its
   * grants are revoked, the agent loses a tool. Both halves of the alternative
   * are worse: acting without the permissions is a privilege escalation, and
   * skipping quietly leaves an admin believing the automation is running.
   *
   * Kept on the row so the reason survives a restart and can be shown next to
   * the switch. Null while nobody has been told anything.
   */
  disabledReason: Schema.NullOr(TrimmedNonEmptyString),
  disabledAt: Schema.NullOr(IsoDateTime),
});
export type QaAutomationProjectSetting = typeof QaAutomationProjectSetting.Type;

/**
 * One row for the whole installation, indexed by project id.
 *
 * One row rather than a key per project because the poller's first question
 * every cycle is "which projects are opted in?", and that has to be one read
 * however many projects exist — a key per project would either be a scan of
 * the settings table or a second place recording which keys exist.
 */
export const QaAutomationSetting = Schema.Struct({
  projects: Schema.Record(Schema.String, QaAutomationProjectSetting),
});
/**
 * There is a **second** key for this feature, `qa-automation-cursor`, and it is
 * deliberately not here: it holds the sweep's "already seen" marks, no client
 * ever reads it, and its shape lives beside the code that owns it
 * (`apps/server/src/qaAutomation/qaAutomationCursor.ts`). Two keys rather than
 * one because this row is edited by an admin from a screen and that one is
 * rewritten by a poll every few minutes.
 */
export type QaAutomationSetting = typeof QaAutomationSetting.Type;

export const AdminQaAutomationResponse = Schema.Struct({
  qaAutomation: QaAutomationSetting,
});
export type AdminQaAutomationResponse = typeof AdminQaAutomationResponse.Type;

/**
 * `PUT /api/admin/system-settings/qa-automation` — one project at a time.
 *
 * A per-project payload rather than the whole `QaAutomationSetting`, which is
 * what the other settings' `PUT` bodies do. The record is installation-wide and
 * this screen edits one row of it: sending the whole document back would make
 * two admins configuring two projects a lost update, and would let a stale tab
 * silently switch off an automation somebody enabled meanwhile.
 *
 * `disabledReason` and `disabledAt` are absent on purpose — they are the
 * server's answer, never the client's claim. Enabling clears them; the server
 * writes them when it turns an automation off by itself.
 */
export const AdminQaAutomationUpdateRequest = Schema.Struct({
  projectId: ProjectId,
  enabled: Schema.Boolean,
  agentId: ManagedAgentId,
  actorUserId: UserId,
  environmentId: EnvironmentId,
  /** Absent means off, so a client written before this existed cannot turn it on. */
  syncOnMerge: Schema.optional(Schema.Boolean).pipe(Schema.withDecodingDefault(() => false)),
});
export type AdminQaAutomationUpdateRequest = typeof AdminQaAutomationUpdateRequest.Type;
